import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { LoginFormData } from '@/lib/validations/auth'

// DEV-only quick-login cards. Each card auto-fills + submits the REAL login form
// (same auth path as a manual sign-in). Email convention: <role-slug>@example.com.
const DEMO_PASSWORD = 'Test@123'

interface DemoAccount {
  role: 'Admin' | 'Manager' | 'Operator'
  email: string
}

const DEMO_ACCOUNTS: DemoAccount[] = [
  { role: 'Admin', email: 'admin@example.com' },
  { role: 'Manager', email: 'manager@example.com' },
  { role: 'Operator', email: 'operator@example.com' },
]

const roleBadge: Record<DemoAccount['role'], 'default' | 'info' | 'success'> = {
  Admin: 'default',
  Manager: 'info',
  Operator: 'success',
}

interface DemoAccountsPanelProps {
  onSelect: (credentials: LoginFormData) => void
  disabled?: boolean
}

export function DemoAccountsPanel({
  onSelect,
  disabled,
}: DemoAccountsPanelProps) {
  const { t } = useTranslation('auth')
  // Entire panel is gated behind import.meta.env.DEV.
  if (!import.meta.env.DEV) return null

  return (
    <div className='mt-6 space-y-2' data-testid='demo-accounts-panel'>
      <p className='text-center text-xs font-medium uppercase tracking-wide text-muted-foreground'>
        {t('demo.heading')}
      </p>
      <div className='grid grid-cols-1 gap-2 sm:grid-cols-3'>
        {DEMO_ACCOUNTS.map(acc => (
          <Card
            key={acc.role}
            role='button'
            tabIndex={0}
            aria-disabled={disabled}
            onClick={() =>
              !disabled &&
              onSelect({ email: acc.email, password: DEMO_PASSWORD })
            }
            onKeyDown={e => {
              if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
                e.preventDefault()
                onSelect({ email: acc.email, password: DEMO_PASSWORD })
              }
            }}
            className='cursor-pointer p-3 transition-colors hover:border-primary'
          >
            <div className='flex items-center justify-between'>
              <Badge variant={roleBadge[acc.role]}>{acc.role}</Badge>
            </div>
            <p className='mt-2 truncate text-xs text-muted-foreground'>
              {acc.email}
            </p>
            <p className='text-xs text-muted-foreground'>{DEMO_PASSWORD}</p>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default DemoAccountsPanel
